import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Info, RefreshCcw, ShieldAlert, AlertOctagon, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import ScoreGauge from "@/components/ScoreGauge";
import FindingCard from "@/components/FindingCard";
import ScanHistory from "@/components/ScanHistory";
import { loadHistory, saveScan, loadVerification } from "@/lib/history";
import { runScan } from "@/lib/api";

const TABS = [
  { key: "all", label: "All", testid: "filter-tab-all" },
  { key: "critical-high", label: "Critical / High", testid: "filter-tab-critical-high" },
  { key: "medium", label: "Medium", testid: "filter-tab-medium" },
  { key: "passed", label: "Passed", testid: "filter-tab-passed" },
];

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const id = params.get("id");

  const [history, setHistory] = useState([]);
  const [scan, setScan] = useState(null);
  const [tab, setTab] = useState("all");
  const [rescanning, setRescanning] = useState(false);

  useEffect(() => {
    const h = loadHistory();
    setHistory(h);
    if (id) {
      const match = h.find((s) => s.id === id);
      setScan(match || null);
    } else {
      setScan(h[0] || null);
    }
  }, [id]);

  const filtered = useMemo(() => {
    if (!scan) return [];
    if (tab === "all") return scan.findings;
    if (tab === "critical-high") return scan.findings.filter((f) => f.severity === "critical" || f.severity === "high");
    if (tab === "medium") return scan.findings.filter((f) => f.severity === "medium");
    if (tab === "passed") return scan.findings.filter((f) => f.severity === "passed");
    return scan.findings;
  }, [scan, tab]);

  const rescan = async () => {
    if (!scan) return;
    setRescanning(true);
    try {
      const domain = scan.domain.toLowerCase();
      const isDemo = ["example.com", "example.net", "example.org"].includes(domain);
      const stored = loadVerification(domain);
      if (!isDemo && !(stored && stored.verified && stored.scanToken)) {
        toast.error("Ownership verification is required to rescan this domain.");
        navigate("/");
        return;
      }
      const result = await runScan({
        domain: scan.domain,
        authorized: true,
        scanToken: isDemo ? null : stored.scanToken,
      });
      const next = saveScan(result);
      setHistory(next);
      setScan(result);
      toast.success("Scan refreshed");
      navigate(`/dashboard?id=${result.id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rescan failed");
    } finally {
      setRescanning(false);
    }
  };

  if (!scan) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-32 max-w-3xl mx-auto px-4 text-center">
          <h1 className="font-display text-3xl font-bold text-slate-100">No scan to show yet</h1>
          <p className="mt-2 text-slate-400">Start one from the home page.</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-5 py-2.5 transition"
            data-testid="back-to-scanner-button"
          >
            <ArrowLeft className="h-4 w-4" /> Back to scanner
          </Link>
        </div>
      </div>
    );
  }

  const stats = [
    { label: "Critical", value: scan.counts.critical, icon: AlertOctagon, color: "text-red-400", bg: "bg-red-500/10 border-red-500/30" },
    { label: "High", value: scan.counts.high, icon: ShieldAlert, color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/30" },
    { label: "Medium", value: scan.counts.medium, icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30" },
    { label: "Passed", value: scan.counts.passed, icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30" },
  ];

  const scannedAt = new Date(scan.scanned_at).toLocaleString();

  return (
    <div className="min-h-screen pb-16" data-testid="dashboard-page">
      <Navbar />
      <div className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300 transition"
              data-testid="back-to-scanner-link"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> New scan
            </Link>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-100 tracking-tight break-all">
              {scan.domain}
            </h1>
            <p className="mt-1 text-xs font-mono text-slate-500">Scanned {scannedAt}</p>
          </div>
          <button
            type="button"
            onClick={rescan}
            disabled={rescanning}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 hover:bg-slate-800 text-slate-200 px-4 py-2 text-sm transition disabled:opacity-60"
            data-testid="rescan-button"
          >
            <RefreshCcw className={`h-4 w-4 ${rescanning ? "animate-spin" : ""}`} />
            {rescanning ? "Rescanning" : "Rescan"}
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Score + summary */}
          <div className="lg:col-span-4">
            <ScoreGauge score={scan.score} grade={scan.grade} />
            <div className="mt-4 grid grid-cols-2 gap-3">
              {stats.map((s) => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.label}
                    className={`rounded-xl border ${s.bg} p-4 flex items-center gap-3`}
                    data-testid={`stat-${s.label.toLowerCase()}`}
                  >
                    <Icon className={`h-5 w-5 ${s.color}`} />
                    <div>
                      <div className={`font-display text-2xl font-bold ${s.color}`}>{s.value}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                        {s.label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-slate-800 bg-slate-900/60 p-4 text-xs text-slate-400 leading-relaxed">
              <Info className="h-4 w-4 text-cyan-400 mt-0.5 shrink-0" />
              <span>{scan.disclaimer}</span>
            </div>
          </div>

          {/* Findings */}
          <div className="lg:col-span-8">
            <div
              className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/60 p-1.5 mb-4"
              role="tablist"
              data-testid="findings-filter-tabs"
            >
              {TABS.map((t) => {
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition ${
                      active
                        ? "bg-cyan-500 text-slate-950"
                        : "text-slate-400 hover:text-slate-100"
                    }`}
                    data-testid={t.testid}
                  >
                    {t.label}
                    <span className={`ml-2 font-mono text-xs ${active ? "text-slate-900/70" : "text-slate-500"}`}>
                      {t.key === "all"
                        ? scan.findings.length
                        : t.key === "critical-high"
                        ? scan.counts.critical + scan.counts.high
                        : scan.counts[t.key]}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="space-y-3" data-testid="findings-list">
              {filtered.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/50 p-8 text-center text-sm text-slate-500">
                  Nothing in this bucket.
                </div>
              ) : (
                filtered.map((f) => <FindingCard key={f.id} finding={f} />)
              )}
            </div>
          </div>
        </div>

        {/* History */}
        <div className="mt-10">
          <ScanHistory history={history} onChange={setHistory} />
        </div>
      </div>
    </div>
  );
}
